/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useMemoizedFn } from 'ahooks';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';

import {
  AntdUploadDragger,
  CheckInputbox,
  DeleteOutlinedIcon,
  DocViewer,
  IntegerInputNumber,
  PrimitiveList,
  PrimitiveListItem,
  PrimitiveListItemMeta,
  QuestionCircleFilledIcon,
} from 'components';
import { useProductAction, useProductValue } from 'context';
import type {
  FileOwnerType,
  ProductFileInfo as ProductFileInfoConfig,
} from 'types';
import {
  FILE,
  generateDownloadFile,
  generateVariableSubProductTitle,
  inputRule,
  validateMediaFile,
} from 'utils';

const {
  FORMAT: {
    PICTURES: PICTURES_FORMAT,
    VIDEOS: VIDEOS_FORMAT,
    DOCUMENTS: DOCUMENTS_FORMAT,
  },
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
} = FILE;

type FieldType = Partial<Record<string, string>>;

interface ProductFileInfoProps extends ProductFileInfoConfig {
  ownerType: FileOwnerType;
  maxIndex: number;
  handleDeleteFile: () => void;
}

const ProductFileInfo = React.memo<ProductFileInfoProps>(
  ({ id, name, title, watermark, ownerType, maxIndex, handleDeleteFile }) => {
    const {
      name: productName,
      productType,
      subProducts = [],
    } = useProductValue();

    return (
      <PrimitiveListItem className='my-5'>
        <PrimitiveListItemMeta
          title={
            <Row>
              <Col span={24} lg={8} className='lg:pe-3'>
                <Row className='max-lg:mb-[--ant-form-item-margin-bottom]'>
                  <DocViewer
                    documents={[
                      {
                        uri: generateDownloadFile(
                          ownerType,
                          id,
                          IMAGE_SIZE_WIDTH,
                          IMAGE_SIZE_HEIGHT,
                        ),
                        fileName: name,
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col span={24} lg={16} className='lg:ps-3'>
                <Row>
                  <Col span={24} lg={12} className='lg:pe-1'>
                    <Space.Compact className='w-full'>
                      <Form.Item<FieldType>
                        label='عنوان'
                        name={`file-index-${id}`}
                        className='basis-1/12 [&_label]:text-nowrap'
                      >
                        <IntegerInputNumber
                          placeholder='رتبه'
                          className='border-e-0'
                          max={maxIndex}
                        />
                      </Form.Item>
                      <Form.Item<FieldType>
                        label=' '
                        name={`file-title-${id}`}
                        className='basis-11/12'
                        rules={[inputRule.string]}
                        initialValue={title}
                      >
                        <Input placeholder='عنوان مستند را وارد کنید' />
                      </Form.Item>
                    </Space.Compact>
                  </Col>
                  <Col span={24} lg={12} className='lg:ps-1'>
                    <Form.Item<FieldType>
                      label='مدل'
                      name={`file-subProduct-${id}`}
                    >
                      <Select
                        allowClear
                        popupMatchSelectWidth={false}
                        placeholder='مدل مستند را انتخاب کنید'
                        options={subProducts.map(
                          ({ id, identifier, attributes }) => ({
                            label: (
                              <Typography.Paragraph className='mb-0'>
                                {productType === 'Normal' ? (
                                  <span>{productName}</span>
                                ) : productType === 'Variable' ? (
                                  attributes.map((attribute, index) => {
                                    const { name, value } =
                                      generateVariableSubProductTitle(
                                        attribute,
                                      );

                                    return (
                                      <React.Fragment key={id}>
                                        <Typography.Text type='secondary'>
                                          {index !== 0 ? (
                                            <span> | </span>
                                          ) : null}
                                          <span>{name}: </span>
                                        </Typography.Text>
                                        <span>{value}</span>
                                      </React.Fragment>
                                    );
                                  })
                                ) : null}
                                {identifier ? (
                                  <Tag className='ms-1'>{identifier}</Tag>
                                ) : null}
                              </Typography.Paragraph>
                            ),
                            value: id,
                          }),
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} lg={12} className='lg:pe-1'>
                    <Form.Item<FieldType>
                      label='نهان‌نگاری (Watermark)'
                      name={`file-watermark-${id}`}
                      valuePropName='checked'
                      initialValue={watermark}
                    >
                      <CheckInputbox>نیاز به نمایش نهان‌نگاری</CheckInputbox>
                    </Form.Item>
                  </Col>
                  <Col span={24} lg={12} className='lg:ps-1'>
                    <Form.Item<FieldType>
                      label=' '
                      className='max-lg:-mt-4 max-lg:mb-0'
                    >
                      <Popconfirm
                        title='تأیید حذف مستند'
                        description='با حذف این مستند، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
                        okButtonProps={{
                          className: 'bg-[--ant-color-error]',
                        }}
                        icon={
                          <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
                        }
                        onConfirm={handleDeleteFile}
                      >
                        <Button
                          danger
                          type='primary'
                          className='w-full'
                          icon={<DeleteOutlinedIcon />}
                        >
                          حذف مستند
                        </Button>
                      </Popconfirm>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          }
        />
      </PrimitiveListItem>
    );
  },
);
ProductFileInfo.displayName = 'ProductFileInfo';

const ProductFilesInfo = () => {
  const [picturesLoading, { set: setPicturesLoading }] = useBoolean();
  const [documentsLoading, { set: setDocumentsLoading }] = useBoolean();
  const { pictures = [], documents = [], ownerType } = useProductValue();
  const action = useProductAction();

  const handleDeletePicture = useMemoizedFn((pictureId: string) => {
    const updatedPictures = pictures.filter(
      picture => picture.id !== pictureId,
    );

    action({
      type: 'UPDATE_PRODUCT_PICTURES',
      payload: {
        pictures: updatedPictures,
      },
    });
  });
  const handleDeleteDocument = useMemoizedFn((documentId: string) => {
    const updatedDocuments = documents.filter(
      document => document.id !== documentId,
    );

    action({
      type: 'UPDATE_PRODUCT_DOCUMENTS',
      payload: {
        documents: updatedDocuments,
      },
    });
  });

  if (!ownerType) return <React.Fragment />;

  const totalPictures = pictures.length;
  const totalDocuments = documents.length;

  return (
    <Row>
      <Col span={24}>
        <Form.Item>
          <AntdUploadDragger
            ownerType={ownerType}
            disabled={picturesLoading || documentsLoading}
            onChange={({ file: { name, status, response } }) => {
              const isPictureFile = validateMediaFile(name, [
                ...PICTURES_FORMAT,
                ...VIDEOS_FORMAT,
              ]);
              const isDocumentFile = validateMediaFile(name, DOCUMENTS_FORMAT);

              if (status === 'uploading') {
                if (isPictureFile) {
                  setPicturesLoading(true);
                }
                if (isDocumentFile) {
                  setDocumentsLoading(true);
                }
              }
              if (status === 'done' && response) {
                if (isPictureFile) {
                  action({
                    type: 'UPDATE_PRODUCT_PICTURES',
                    payload: {
                      pictures: [...pictures, response],
                    },
                  });
                }
                if (isDocumentFile) {
                  action({
                    type: 'UPDATE_PRODUCT_DOCUMENTS',
                    payload: {
                      documents: [...documents, response],
                    },
                  });
                }
              }
              if (status !== 'uploading') {
                if (isPictureFile) {
                  setPicturesLoading(false);
                }
                if (isDocumentFile) {
                  setDocumentsLoading(false);
                }
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Typography.Title level={4} className='text-sm font-medium'>
          تصاویر و ویدیو‌ها
        </Typography.Title>
        <PrimitiveList
          loading={{
            spinning: picturesLoading,
          }}
          dataSource={pictures}
          renderItem={item => {
            const { id } = item;
            return (
              <ProductFileInfo
                ownerType={ownerType}
                maxIndex={totalPictures}
                handleDeleteFile={() => {
                  handleDeletePicture(id);
                }}
                {...item}
              />
            );
          }}
        />
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-5 mt-2' />
      </Col>
      <Col span={24}>
        <Typography.Title level={4} className='text-sm font-medium'>
          فایل‌ها
        </Typography.Title>
        <PrimitiveList
          loading={{
            spinning: documentsLoading,
          }}
          dataSource={documents}
          renderItem={item => {
            const { id } = item;
            return (
              <ProductFileInfo
                ownerType={ownerType}
                maxIndex={totalDocuments}
                handleDeleteFile={() => {
                  handleDeleteDocument(id);
                }}
                {...item}
              />
            );
          }}
        />
      </Col>
    </Row>
  );
};

export { ProductFileInfo, ProductFilesInfo };
